<template>
  <v-list-item
    :class="[
      'notification-item',
      { 'notification-item--new': isNew }
    ]"
  >
    <v-list-item-avatar class="notification-item__icon">
      <v-icon :color="iconColor" size="24">{{ icon }}</v-icon>
    </v-list-item-avatar>

    <v-list-item-content>
      <v-list-item-title class="notification-item__text">
        {{ notification.text }}
      </v-list-item-title>
      <v-list-item-subtitle class="notification-item__time">
        {{ formattedTime }}
      </v-list-item-subtitle>
    </v-list-item-content>

    <div v-if="isNew" class="notification-item__new-dot" aria-hidden="true"></div>

    <v-list-item-action v-if="dismissible" class="notification-item__actions">
      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            small
            v-bind="attrs"
            v-on="on"
            @click.stop
          >
            <v-icon small>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense class="notification-item__menu">
          <v-list-item
            class="notification-item__menu-item"
            @click="$emit('dismiss', notification.id)"
          >
            <v-list-item-icon>
              <v-icon small>mdi-close</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('notifications-dismiss') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
export default {
  name: 'NotificationItem',
  props: {
    notification: {
      type: Object,
      required: true,
    },
    lastSeenAt: {
      type: String,
      default: null,
    },
    dismissible: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    isNew() {
      if (!this.lastSeenAt || !this.notification.startTime) return false;
      const seen = new Date(this.lastSeenAt).getTime();
      const start = new Date(this.notification.startTime).getTime();
      if (Number.isNaN(seen) || Number.isNaN(start)) return false;
      return start > seen;
    },
    notificationType() {
      const type = this.notification.notificationType;
      return type ? String(type).toLowerCase() : 'info';
    },
    icon() {
      if (this.notificationType === 'critical') return 'mdi-alert-circle';
      if (this.notificationType === 'warning') return 'mdi-alert';
      return 'mdi-information';
    },
    iconColor() {
      if (this.notificationType === 'critical') return 'error';
      if (this.notificationType === 'warning') return 'warning';
      return 'info';
    },
    formattedTime() {
      if (!this.notification.startTime) return '';
      try {
        const date = new Date(this.notification.startTime);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return this.$t('notifications-just-now');
        if (diffMins < 60) return this.$tc('notifications-minutes-ago', diffMins, { count: diffMins });
        if (diffHours < 24) return this.$tc('notifications-hours-ago', diffHours, { count: diffHours });
        if (diffDays < 30) return this.$tc('notifications-days-ago', diffDays, { count: diffDays });
        
        return date.toLocaleDateString();
      } catch (e) {
        return '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.notification-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &--new {
    background-color: rgba(33, 150, 243, 0.06);
  }
}

.notification-item__new-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #1976d2;
  margin-right: 8px;
}

.notification-item__icon {
  margin-right: 12px;
}

.notification-item__text {
  font-size: 14px;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
}

.notification-item__time {
  font-size: 12px;
  margin-top: 4px;
}

.notification-item__actions {
  margin-left: 8px;
}

.notification-item__menu-item {
  justify-content: center;
}

.notification-item__menu {
  ::v-deep .v-list-item {
    justify-content: center;
    padding-left: 16px;
    padding-right: 16px;
  }
  ::v-deep .v-list-item__icon {
    min-width: auto;
    margin: 0 8px 0 0;
  }
  ::v-deep .v-list-item__content {
    flex: 0 1 auto;
  }
}
</style>
